/**
 * CameraRig - o dono do olhar em primeira pessoa (secao 4.10 do ARCHITECTURE.md).
 *
 * Este modulo e o que separa um FPS que "parece jogo" de um que parece demo
 * tecnico. A regra que guia tudo aqui: NENHUMA camada e aplicada crua. Toda
 * contribuicao passa por mola ou por amortecimento exponencial, e o resultado e
 * escrito em ctx.camera UMA UNICA VEZ por frame, no fim do update.
 *
 * Ordem de composicao por frame (nao inverter - a ordem muda a sensacao):
 *   1. olhar do mouse (yaw/pitch base, unico estado "autoritativo")
 *   2. recuo da arma (empurra a MIRA, nao so a imagem)
 *   3. sway + head bob + roll + agachamento + impacto de aterrissagem
 *   4. screen shake (ruido, nunca senoide)
 *   5. escrita em ctx.camera (rotacao YXZ + posicao) e FOV
 *
 * O CameraRig roda DEPOIS do WeaponSystem no main.js. Isso e proposital: o
 * recuo gerado pelo tiro deste frame ja entra na camera deste mesmo frame.
 *
 * Alocacao por frame: zero. Todo temporario e escalar ou vive no escopo do
 * modulo. O unico objeto criado por frame vem de input.consumeMouseDelta(),
 * que pertence ao Input e nao pode ser alterado por este modulo.
 */

import { PLAYER } from '../core/Constants.js';

const DEG = Math.PI / 180;
const TWO_PI = Math.PI * 2;

// --- limites do olhar -------------------------------------------------------
const PITCH_LIMIT = 88 * DEG;        // travamento do olhar do jogador (contrato)
const PITCH_LIMIT_HARD = 89.5 * DEG; // travamento do resultado JA somado com as camadas

// --- sway (a camera atrasa uma fracao do giro e volta com mola) --------------
const SWAY_FOLLOW = 0.32;            // fracao do giro do mouse que fica para tras
const SWAY_MAX = 0.055;              // rad (~3.1 graus): flick violento nao inverte a imagem
const SWAY_RETURN = 9.5;             // lambda da volta ao centro

// --- head bob em figura de oito ---------------------------------------------
// A fase avanca com a DISTANCIA percorrida, nao com o tempo. Andar devagar da um
// bob devagar e parar congela o ciclo, em vez de continuar balancando no vazio.
const BOB_CYCLES_PER_METER = 0.36;   // ~3 passos/s andando (2 passos por ciclo)
const BOB_X = 0.026;                 // metros, deslocamento lateral (a metade da frequencia)
const BOB_Y = 0.018;                 // metros, deslocamento vertical (o dobro da frequencia)
const BOB_ROLL = 0.0115;             // rad, a cabeca acompanha o balanco lateral
const BOB_PITCH = 0.0062;            // rad, aceno vertical no passo
const BOB_LAMBDA = 7.5;              // subida/descida da amplitude (some ao parar)

// --- roll --------------------------------------------------------------------
const ROLL_STRAFE = 0.044;           // rad (~2.5 graus) andando de lado na velocidade cheia
const ROLL_TURN_GAIN = 0.013;        // rad por rad/s de giro
const ROLL_TURN_MAX = 0.026;         // rad (~1.5 graus) girando rapido
const ROLL_MAX = 0.058;              // rad (~3.3 graus) somando tudo
const ROLL_LAMBDA = 8.5;

// --- recuo local (addRecoil) - mola levemente subamortecida ------------------
const REC_STIFF = 190;
const REC_DAMP = 22.0;               // ~0.8 de amortecimento: sobe seco e assenta sem tremer
const REC_MAX = 0.30;                // rad, teto de seguranca

// O recuo vindo do WeaponSystem e um offset ABSOLUTO (ele tem recuperacao propria).
// Um lambda alto deixa o valor praticamente instantaneo - so serve para nao
// propagar um degrau caso a arma zere o recuo de uma vez.
const WEAPON_RECOIL_LAMBDA = 95;

// --- altura do olho / agachamento -------------------------------------------
const EYE_STIFF = 210;
const EYE_DAMP = 23.5;               // subamortecido de proposito: o olho desce em curva

// --- impacto de aterrissagem -------------------------------------------------
const LAND_MIN_SPEED = 1.6;          // m/s abaixo disso e so uma pisada
const LAND_REF_SPEED = 10.5;         // m/s = impacto maximo
const LAND_IMPULSE = 3.2;            // m/s de impulso no olho, no impacto maximo
const LAND_STIFF = 132;
const LAND_DAMP = 17.0;
const LAND_MIN = -0.22;              // teto de mergulho (metros)
const LAND_MAX = 0.10;               // teto do repique para cima
const LAND_PITCH_GAIN = 0.92;        // rad por metro de mergulho (nariz para baixo)
const JUMP_IMPULSE = 0.55;           // m/s: a cabeca sobe de leve ao saltar

// --- screen shake ------------------------------------------------------------
const SHAKE_SLOTS = 6;               // pool fixo: zero alocacao e concorrencia correta
const SHAKE_FREQ = 23.0;             // frequencia do ruido
const SHAKE_YAW = 0.042;             // rad em amplitude 1 (explosao ao lado)
const SHAKE_PITCH = 0.048;
const SHAKE_ROLL = 0.075;
const SHAKE_POS = 0.070;             // metros
const SHAKE_SUM_MAX = 1.25;

// --- respiracao / ruido de mao parada ---------------------------------------
const BREATH_Y = 0.0058;             // metros
const IDLE_NOISE_YAW = 0.0017;       // rad (~0.1 grau) - deriva de mao, nao atrapalha a mira
const IDLE_NOISE_PITCH = 0.0014;

// --- FOV ---------------------------------------------------------------------
const SPRINT_FOV_KICK = 7.5;         // graus somados na velocidade de corrida
const SPRINT_FOV_LAMBDA = 5.5;
const ADS_TIME_FALLBACK = 0.22;      // s, quando a arma nao informa
const ADS_ZOOM_FALLBACK = 1.18;      // mira de ferro discreta
const FOV_EPSILON = 0.01;            // so recalcula a projecao quando muda de verdade

// --- morte -------------------------------------------------------------------
const DEATH_ROLL = 0.30;             // rad (~17 graus)
const DEATH_PITCH = 0.20;            // rad, olhar cai
const DEATH_EYE = 0.38;              // metros do chao onde a cabeca para

// ---------------------------------------------------------------------------
// utilitarios de escopo de modulo (nenhum aloca)
// ---------------------------------------------------------------------------

function clamp(v, min, max) { return v < min ? min : (v > max ? max : v); }
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
function smoothstep(t) { return t * t * (3 - 2 * t); }
function num(v, fallback) { return typeof v === 'number' && Number.isFinite(v) ? v : fallback; }

/** Amortecimento exponencial: mesmo resultado a 30 ou a 240 fps. */
function damp(current, target, lambda, dt) {
  return target + (current - target) * Math.exp(-lambda * dt);
}

/* Ruido de valor 1D. Screen shake com senoide fica com cara de vibracao de
 * celular; o ruido interpolado da o tremor irregular que se espera de impacto. */
function hash1(n) {
  const s = Math.sin(n * 127.1) * 43758.5453123;
  return s - Math.floor(s);
}
function noise1(x) {
  const i = Math.floor(x);
  const f = x - i;
  const u = f * f * (3 - 2 * f);
  const a = hash1(i);
  const b = hash1(i + 1);
  return (a + (b - a) * u) * 2 - 1;   // -1..1
}
function fbm1(x) {
  return noise1(x) * 0.62 + noise1(x * 2.31 + 19.7) * 0.38;
}

/**
 * Integrador de mola massa-mola-amortecedor. JS nao passa escalar por
 * referencia, entao a saida sai neste par fixo de modulo: [posicao, velocidade].
 * Sub-passos garantem estabilidade se o frame engasgar.
 */
const _spring = [0, 0];
function integrateSpring(pos, vel, target, stiffness, damping, dt) {
  const steps = dt > 0.02 ? (dt > 0.04 ? 4 : 2) : 1;
  const h = dt / steps;
  for (let i = 0; i < steps; i++) {
    vel += (-(pos - target) * stiffness - vel * damping) * h;
    pos += vel * h;
  }
  _spring[0] = pos;
  _spring[1] = vel;
}

export class CameraRig {
  constructor(ctx) {
    this.ctx = ctx;
    this.enabled = true;

    // Olhar base: o unico estado autoritativo. Todo o resto e offset aditivo.
    this.yaw = Math.PI;
    this.pitch = 0;

    this._time = 0;
    this._warned = false;

    // --- sway ---
    this._swayYaw = 0;
    this._swayPitch = 0;
    this._yawRate = 0;

    // --- recuo ---
    this._recPitch = 0;   // mola local alimentada por addRecoil()
    this._recPitchVel = 0;
    this._recYaw = 0;
    this._recYawVel = 0;
    this._wRecPitch = 0;  // copia amortecida do offset do WeaponSystem
    this._wRecYaw = 0;

    // --- bob / roll ---
    this._bobPhase = 0;
    this._bobAmt = 0;
    this._rollSm = 0;
    this._offX = 0;       // offset local suavizado (lateral, no espaco da camera)
    this._offY = 0;       // offset local suavizado (vertical)

    // --- altura do olho ---
    this._eyeH = PLAYER.EYE_HEIGHT;
    this._eyeVel = 0;

    // --- aterrissagem ---
    this._landP = 0;
    this._landVel = 0;
    this._wasGrounded = true;
    this._fallSpeed = 0;
    this._landCooldown = 0;

    // --- shake (pool fixo de envelopes) ---
    this._shakes = new Array(SHAKE_SLOTS);
    for (let i = 0; i < SHAKE_SLOTS; i++) this._shakes[i] = { a: 0, d: 1, t: 0, on: false };

    // --- mira / FOV ---
    this._adsT = 0;
    this._sprintFov = 0;

    // --- morte ---
    this._deathT = 0;

    // --- fallback de velocidade quando o controller nao expoe .velocity ---
    this._prevX = 0; this._prevY = 0; this._prevZ = 0;
    this._hasPrev = false;
    this._velX = 0; this._velY = 0; this._velZ = 0;

    // --- ultima posicao de olho valida (usada se o controller sumir) ---
    this._eyeX = 0; this._eyeY = PLAYER.EYE_HEIGHT; this._eyeZ = 0;

    // --- pose de depuracao ---
    // setPose() congela a camera no ponto pedido para o print sair estavel.
    // Sai desse modo assim que o jogador de fato joga (mouse ou teclas).
    this._posed = false;
    this._posedX = 0; this._posedY = 0; this._posedZ = 0;

    this._unsubs = [];
  }

  async init() {
    const ctx = this.ctx;
    const E = ctx.EVENTS;
    if (!ctx.events || !E) return;

    // Aterrissagem: a deteccao propria (transicao de grounded) e a fonte
    // preferida porque a unidade e conhecida (m/s). O evento entra so como rede
    // de seguranca, e o cooldown impede contar o mesmo pouso duas vezes.
    this._unsubs.push(ctx.events.on(E.PLAYER_LAND, (p) => {
      if (this._landCooldown > 0) return;
      const raw = num(p && p.impact, 0);
      // O contrato nao fixa a unidade de "impact": trato valor grande como m/s
      // e valor pequeno como fator normalizado.
      const speed = raw > 1.5 ? raw : raw * 12;
      this._applyLanding(speed);
    }));

    this._unsubs.push(ctx.events.on(E.PLAYER_JUMP, () => {
      this._landVel += JUMP_IMPULSE;
    }));

    this._unsubs.push(ctx.events.on(E.PLAYER_DAMAGED, (p) => {
      const amount = clamp01(num(p && p.amount, 10) / 45);
      this.addShake(0.09 + amount * 0.52, 0.20 + amount * 0.30);
      this._hitKick(p && p.from, amount);
    }));

    // Tremor minimo por disparo. O grosso da sensacao vem do recuo; isto so
    // acrescenta a vibracao que o recuo sozinho nao da.
    this._unsubs.push(ctx.events.on(E.WEAPON_FIRE, () => {
      this.addShake(0.05, 0.13);
    }));

    // Ao trocar de estado (pausar, retomar, morrer) o update pode ficar parado
    // enquanto o mouse ainda acumula. Drenar aqui evita o giro seco na volta.
    this._unsubs.push(ctx.events.on(E.GAME_STATE, () => {
      if (ctx.input && ctx.input.consumeMouseDelta) ctx.input.consumeMouseDelta();
    }));

    const cam = ctx.camera;
    if (cam) {
      this._eyeX = cam.position.x;
      this._eyeY = cam.position.y;
      this._eyeZ = cam.position.z;
    }
  }

  // -------------------------------------------------------------------------
  // API publica
  // -------------------------------------------------------------------------

  getYaw() { return this.yaw; }
  getPitch() { return this.pitch; }

  /**
   * Teleporta o olhar. `pos` e a posicao da CAMERA (altura do olho), como
   * manda o DebugApi. Zera todas as camadas transitorias para o quadro sair
   * limpo e estavel: print de revisao visual nao pode pegar a camera no meio de
   * um balanco.
   */
  setPose(pos, yaw, pitch) {
    if (pos) {
      this._posedX = num(pos.x, this._posedX);
      this._posedY = num(pos.y, this._posedY);
      this._posedZ = num(pos.z, this._posedZ);
      this._eyeX = this._posedX;
      this._eyeY = this._posedY;
      this._eyeZ = this._posedZ;
      this._posed = true;
    }
    this.yaw = num(yaw, this.yaw);
    this.pitch = clamp(num(pitch, this.pitch), -PITCH_LIMIT, PITCH_LIMIT);

    this._swayYaw = 0; this._swayPitch = 0; this._yawRate = 0;
    this._recPitch = 0; this._recPitchVel = 0;
    this._recYaw = 0; this._recYawVel = 0;
    this._wRecPitch = 0; this._wRecYaw = 0;
    this._bobAmt = 0; this._rollSm = 0;
    this._offX = 0; this._offY = 0;
    this._landP = 0; this._landVel = 0; this._landCooldown = 0;
    this._fallSpeed = 0; this._wasGrounded = true;
    this._sprintFov = 0; this._adsT = 0; this._deathT = 0;
    this._hasPrev = false;
    this._velX = 0; this._velY = 0; this._velZ = 0;
    for (let i = 0; i < SHAKE_SLOTS; i++) this._shakes[i].on = false;

    const c = this.ctx.controller;
    const eyeTarget = num(c && c.eyeHeight, PLAYER.EYE_HEIGHT);
    this._eyeH = eyeTarget;
    this._eyeVel = 0;

    // Escreve ja, para quem tirar o print antes do proximo update ver a pose certa.
    const cam = this.ctx.camera;
    if (cam) {
      cam.position.set(this._eyeX, this._eyeY, this._eyeZ);
      cam.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
      const baseFov = clamp(num(this.ctx.settings && this.ctx.settings.fov, 80), 40, 130);
      if (Math.abs(cam.fov - baseFov) > FOV_EPSILON) {
        cam.fov = baseFov;
        cam.updateProjectionMatrix();
      }
      cam.updateMatrixWorld();
    }
  }

  /**
   * Tremor de tela. `intensity` ~0.05 para um tiro, ~0.35 para levar dano,
   * ~1.0 para explosao ao lado. Cada chamada ocupa um envelope proprio do pool,
   * entao rajada longa satura suave em vez de acumular ate a tela explodir.
   */
  addShake(intensity, duration) {
    const a = clamp(num(intensity, 0), 0, 1.5);
    if (a <= 0.0005) return;
    const d = clamp(num(duration, 0.3), 0.04, 4);

    let slot = -1;
    let weakest = -1;
    let weakestValue = Infinity;
    for (let i = 0; i < SHAKE_SLOTS; i++) {
      const s = this._shakes[i];
      if (!s.on) { slot = i; break; }
      const u = clamp01(s.t / s.d);
      const remaining = s.a * (1 - u) * (1 - u);
      if (remaining < weakestValue) { weakestValue = remaining; weakest = i; }
    }
    if (slot < 0) {
      // Todos ocupados: so rouba a vaga se este tremor for mais forte que o
      // mais fraco em curso - assim um tiro nao apaga uma explosao.
      if (weakest < 0 || a <= weakestValue) return;
      slot = weakest;
    }
    const s = this._shakes[slot];
    s.a = a; s.d = d; s.t = 0; s.on = true;
  }

  /**
   * Recuo local, em radianos. Positivo em `pitchRad` levanta a mira; positivo
   * em `yawRad` empurra para a esquerda. Vai para a velocidade da mola: o
   * impulso sobe seco e a recuperacao volta sozinha.
   */
  addRecoil(pitchRad, yawRad) {
    this._recPitchVel += num(pitchRad, 0) * 26;
    this._recYawVel += num(yawRad, 0) * 26;
  }

  // -------------------------------------------------------------------------
  // laco de frame
  // -------------------------------------------------------------------------

  update(dt) {
    if (!Number.isFinite(dt) || dt <= 0) return;
    const step = dt > 0.05 ? 0.05 : dt;   // frame longo nao pode explodir as molas
    try {
      this._update(step);
    } catch (err) {
      if (!this._warned) {
        this._warned = true;
        console.warn('[CameraRig] falhou e vai seguir degradado:', err);
      }
    }
  }

  _update(dt) {
    const ctx = this.ctx;
    const settings = ctx.settings;
    const input = ctx.input;
    const camera = ctx.camera;
    if (!camera) return;

    this._time += dt;
    if (this._landCooldown > 0) this._landCooldown -= dt;

    const bobScale = clamp(num(settings && settings.cameraBob, 1), 0, 2);
    const shakeScale = clamp(num(settings && settings.screenShake, 1), 0, 2);

    // --- 1. estado da mira (dita sensibilidade, FOV e amplitude do bob) -----
    const weapons = ctx.weapons;
    const isADS = !!(weapons && weapons.isADS);
    const reported = weapons && weapons.getAdsProgress ? weapons.getAdsProgress() : undefined;
    if (typeof reported === 'number' && Number.isFinite(reported)) {
      // Se a arma informa o proprio progresso, a camera casa exatamente com ela.
      this._adsT = clamp01(reported);
    } else {
      const rate = 1 / Math.max(0.06, this._adsTime());
      const target = isADS ? 1 : 0;
      const delta = rate * dt;
      this._adsT = target > this._adsT
        ? Math.min(target, this._adsT + delta)
        : Math.max(target, this._adsT - delta);
    }
    const ads = smoothstep(this._adsT);

    // --- 2. olhar do mouse --------------------------------------------------
    // A sensibilidade da mira entra interpolada, nao em degrau: trocar de
    // sensibilidade no meio de um arrasto e o que faz a mira "pular".
    const adsSens = clamp(num(settings && settings.adsSensitivityScale, 0.65), 0.1, 2);
    const sensScale = 1 + (adsSens - 1) * ads;

    let mdx = 0;
    let mdy = 0;
    if (input && input.consumeMouseDelta) {
      const d = input.consumeMouseDelta(sensScale);   // sempre drenar, mesmo sem aplicar
      if (d) { mdx = num(d.x, 0); mdy = num(d.y, 0); }
    }
    if (!this.enabled) { mdx = 0; mdy = 0; }

    if (this._posed && (mdx !== 0 || mdy !== 0 || this._playerIsPlaying(input))) {
      this._posed = false;   // o jogador assumiu o controle: solta a pose fixa
    }

    this.yaw -= mdx;
    this.pitch = clamp(this.pitch - mdy, -PITCH_LIMIT, PITCH_LIMIT);
    // mantem yaw em faixa util (sessao longa perderia precisao de float)
    if (this.yaw > TWO_PI) this.yaw -= TWO_PI;
    else if (this.yaw < -TWO_PI) this.yaw += TWO_PI;

    // --- 3. recuo -----------------------------------------------------------
    // (a) offset absoluto vindo da arma. Some ao OLHAR, entao empurra a mira de
    //     verdade - a proxima bala sai do novo angulo, nao so a imagem sobe.
    let wPitch = 0;
    let wYaw = 0;
    if (weapons && weapons.getRecoilOffset) {
      const r = weapons.getRecoilOffset();
      if (r) {
        // Aceita {pitch,yaw} ou o Vector2 do contrato (x = horizontal, y = vertical).
        if (typeof r.pitch === 'number') { wPitch = num(r.pitch, 0); wYaw = num(r.yaw, 0); }
        else { wYaw = num(r.x, 0); wPitch = num(r.y, 0); }
      }
    }
    this._wRecPitch = damp(this._wRecPitch, clamp(wPitch, -REC_MAX, REC_MAX), WEAPON_RECOIL_LAMBDA, dt);
    this._wRecYaw = damp(this._wRecYaw, clamp(wYaw, -REC_MAX, REC_MAX), WEAPON_RECOIL_LAMBDA, dt);

    // (b) mola local (addRecoil / impacto de dano)
    integrateSpring(this._recPitch, this._recPitchVel, 0, REC_STIFF, REC_DAMP, dt);
    this._recPitch = clamp(_spring[0], -REC_MAX, REC_MAX);
    this._recPitchVel = _spring[1];
    integrateSpring(this._recYaw, this._recYawVel, 0, REC_STIFF, REC_DAMP, dt);
    this._recYaw = clamp(_spring[0], -REC_MAX, REC_MAX);
    this._recYawVel = _spring[1];

    const recoilPitch = this._wRecPitch + this._recPitch;
    const recoilYaw = this._wRecYaw + this._recYaw;

    // --- 4. sway (a camera fica para tras do giro e volta com mola) ---------
    const swayScale = 0.4 + 0.6 * bobScale;
    this._swayYaw = clamp(this._swayYaw + mdx * SWAY_FOLLOW * swayScale, -SWAY_MAX, SWAY_MAX);
    this._swayPitch = clamp(this._swayPitch + mdy * SWAY_FOLLOW * swayScale, -SWAY_MAX, SWAY_MAX);
    this._swayYaw = damp(this._swayYaw, 0, SWAY_RETURN, dt);
    this._swayPitch = damp(this._swayPitch, 0, SWAY_RETURN, dt);

    // velocidade angular suavizada (alimenta o roll de virada)
    this._yawRate = damp(this._yawRate, -mdx / Math.max(dt, 0.004), 13, dt);

    // --- 5. estado de movimento --------------------------------------------
    const controller = ctx.controller;
    let feetX = this._eyeX;
    let feetY = this._eyeY - this._eyeH;
    let feetZ = this._eyeZ;
    let hasFeet = false;
    const pos = controller && controller.position;
    if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y) && Number.isFinite(pos.z)) {
      feetX = pos.x; feetY = pos.y; feetZ = pos.z;
      hasFeet = true;
    }

    let vx = 0;
    let vy = 0;
    let vz = 0;
    const vel = controller && controller.velocity;
    if (vel && Number.isFinite(vel.x) && Number.isFinite(vel.y) && Number.isFinite(vel.z)) {
      vx = vel.x; vy = vel.y; vz = vel.z;
    } else if (hasFeet) {
      // Fallback: deriva a velocidade da posicao. Serve para o rig continuar
      // com bob e roll corretos mesmo com um controller que nao expoe velocity.
      if (this._hasPrev) {
        const inv = 1 / dt;
        this._velX = damp(this._velX, (feetX - this._prevX) * inv, 18, dt);
        this._velY = damp(this._velY, (feetY - this._prevY) * inv, 18, dt);
        this._velZ = damp(this._velZ, (feetZ - this._prevZ) * inv, 18, dt);
      }
      this._prevX = feetX; this._prevY = feetY; this._prevZ = feetZ;
      this._hasPrev = true;
      vx = this._velX; vy = this._velY; vz = this._velZ;
    }

    const horizSpeed = Math.sqrt(vx * vx + vz * vz);
    const grounded = !controller || controller.grounded !== false;

    // eixo "direita" da camera no plano XZ (yaw puro): frente = (-sin, 0, -cos)
    const cy = Math.cos(this.yaw);
    const sy = Math.sin(this.yaw);
    const rightX = cy;
    const rightZ = -sy;
    const lateral = vx * rightX + vz * rightZ;

    // --- 6. aterrissagem ----------------------------------------------------
    if (!grounded) {
      if (vy < this._fallSpeed) this._fallSpeed = vy;
    } else if (!this._wasGrounded) {
      this._applyLanding(-this._fallSpeed);
      this._fallSpeed = 0;
    } else {
      this._fallSpeed = 0;
    }
    this._wasGrounded = grounded;

    integrateSpring(this._landP, this._landVel, 0, LAND_STIFF, LAND_DAMP, dt);
    this._landP = clamp(_spring[0], LAND_MIN, LAND_MAX);
    this._landVel = _spring[1];
    const landScale = 0.35 + 0.65 * bobScale;   // feedback de impacto nunca some de todo
    const landDip = this._landP * landScale;
    const landPitch = landDip * LAND_PITCH_GAIN;

    // --- 7. head bob em figura de oito --------------------------------------
    const bobTarget = grounded ? clamp01(horizSpeed / PLAYER.WALK_SPEED) : 0;
    this._bobAmt = damp(this._bobAmt, bobTarget, BOB_LAMBDA, dt);
    if (grounded) {
      this._bobPhase += horizSpeed * BOB_CYCLES_PER_METER * TWO_PI * dt;
      if (this._bobPhase > TWO_PI) this._bobPhase -= TWO_PI;
    }
    // Correr aumenta a amplitude; mirar quase elimina (nao da para mirar
    // com a tela balancando).
    const bobAmp = this._bobAmt * bobScale * (0.85 + 0.35 * clamp01(horizSpeed / PLAYER.SPRINT_SPEED)) * (1 - 0.68 * ads);
    const ph = this._bobPhase;
    const bobX = Math.sin(ph) * BOB_X * bobAmp;
    const bobY = Math.sin(ph * 2) * BOB_Y * bobAmp;          // dobro da frequencia = figura de oito
    const bobRoll = Math.sin(ph) * BOB_ROLL * bobAmp;
    const bobPitch = Math.sin(ph * 2 + 0.7) * BOB_PITCH * bobAmp;

    // --- 8. respiracao e deriva de mao (so com o jogador quase parado) ------
    const idle = (1 - clamp01(this._bobAmt)) * bobScale;
    const breathY = Math.sin(this._time * 1.35) * BREATH_Y * idle;
    const idleFade = idle * (1 - 0.55 * ads);
    const idleYaw = fbm1(this._time * 0.45) * IDLE_NOISE_YAW * idleFade;
    const idlePitch = fbm1(this._time * 0.41 + 31.7) * IDLE_NOISE_PITCH * idleFade;

    // --- 9. roll ------------------------------------------------------------
    const rollStrafe = -clamp(lateral / PLAYER.WALK_SPEED, -1, 1) * ROLL_STRAFE;
    const rollTurn = clamp(this._yawRate * ROLL_TURN_GAIN, -ROLL_TURN_MAX, ROLL_TURN_MAX);
    const rollTarget = clamp((rollStrafe + rollTurn) * bobScale * (1 - 0.6 * ads) + bobRoll, -ROLL_MAX, ROLL_MAX);
    this._rollSm = damp(this._rollSm, rollTarget, ROLL_LAMBDA, dt);

    // --- 10. altura do olho / agachamento -----------------------------------
    let eyeTarget = controller ? controller.eyeHeight : undefined;
    if (!Number.isFinite(eyeTarget)) {
      eyeTarget = (controller && controller.crouching) ? PLAYER.CROUCH_EYE_HEIGHT : PLAYER.EYE_HEIGHT;
    }
    integrateSpring(this._eyeH, this._eyeVel, eyeTarget, EYE_STIFF, EYE_DAMP, dt);
    this._eyeH = clamp(_spring[0], 0.2, 2.4);
    this._eyeVel = _spring[1];

    // --- 11. screen shake ---------------------------------------------------
    let shakeSum = 0;
    for (let i = 0; i < SHAKE_SLOTS; i++) {
      const s = this._shakes[i];
      if (!s.on) continue;
      s.t += dt;
      if (s.t >= s.d) { s.on = false; continue; }
      const k = 1 - s.t / s.d;
      shakeSum += s.a * k * k;          // decaimento quadratico: forte no inicio, cauda macia
    }
    let shakeYaw = 0;
    let shakePitch = 0;
    let shakeRoll = 0;
    let shakePosX = 0;
    let shakePosY = 0;
    if (shakeSum > 0.0005) {
      const amp = Math.min(shakeSum, SHAKE_SUM_MAX) * shakeScale;
      const t = this._time * SHAKE_FREQ;
      shakeYaw = fbm1(t) * SHAKE_YAW * amp;
      shakePitch = fbm1(t + 17.3) * SHAKE_PITCH * amp;
      shakeRoll = fbm1(t * 0.83 + 41.1) * SHAKE_ROLL * amp;
      shakePosX = fbm1(t * 1.27 + 61.7) * SHAKE_POS * amp;
      shakePosY = fbm1(t * 1.19 + 83.5) * SHAKE_POS * amp;
    }

    // --- 12. morte ----------------------------------------------------------
    const dead = !!(ctx.player && ctx.player.alive === false);
    this._deathT = damp(this._deathT, dead ? 1 : 0, dead ? 3.4 : 6.5, dt);
    const death = smoothstep(clamp01(this._deathT));
    const deathRoll = death * DEATH_ROLL;
    const deathPitch = -death * DEATH_PITCH;
    const deathDrop = -death * Math.max(0, this._eyeH - DEATH_EYE);

    // --- 13. composicao (as camadas lentas entram suavizadas; o shake, cru) --
    this._offX = damp(this._offX, bobX, 26, dt);
    this._offY = damp(this._offY, bobY + breathY, 26, dt);

    let finalYaw = this.yaw + this._swayYaw + recoilYaw + idleYaw + shakeYaw;
    let finalPitch = this.pitch + this._swayPitch + recoilPitch + bobPitch + landPitch
      + idlePitch + deathPitch + shakePitch;
    finalPitch = clamp(finalPitch, -PITCH_LIMIT_HARD, PITCH_LIMIT_HARD);
    const finalRoll = this._rollSm + shakeRoll + deathRoll;

    if (this._posed) {
      // Pose de depuracao: imagem parada, sem nenhuma camada, para o print
      // sair igual em qualquer frame.
      camera.position.set(this._posedX, this._posedY, this._posedZ);
      camera.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
      camera.updateMatrixWorld();
      this._applyFov(camera, settings, 0, 0, true);
      return;
    }

    if (hasFeet) {
      this._eyeX = feetX;
      this._eyeY = feetY + this._eyeH;
      this._eyeZ = feetZ;
    }

    const localX = this._offX + shakePosX;
    const localY = this._offY + shakePosY + landDip + deathDrop;
    const px = this._eyeX + rightX * localX;
    const py = this._eyeY + localY;
    const pz = this._eyeZ + rightZ * localX;

    if (Number.isFinite(px) && Number.isFinite(py) && Number.isFinite(pz)) {
      camera.position.set(px, py, pz);
    }
    camera.rotation.set(finalPitch, finalYaw, finalRoll, 'YXZ');
    // Deixa a matriz pronta para quem le a camera depois (viewmodel, fx, audio).
    camera.updateMatrixWorld();

    // --- 14. FOV ------------------------------------------------------------
    const sprintNorm = grounded
      ? clamp01((horizSpeed - PLAYER.WALK_SPEED * 0.9) / Math.max(0.5, PLAYER.SPRINT_SPEED - PLAYER.WALK_SPEED * 0.9))
      : 0;
    this._sprintFov = damp(this._sprintFov, sprintNorm * SPRINT_FOV_KICK * (1 - ads), SPRINT_FOV_LAMBDA, dt);
    this._applyFov(camera, settings, ads, this._sprintFov, false);
  }

  // -------------------------------------------------------------------------
  // internos
  // -------------------------------------------------------------------------

  /** FOV = base, puxado para o valor de mira e somado ao kick de corrida. */
  _applyFov(camera, settings, ads, sprintKick, forceBase) {
    const baseFov = clamp(num(settings && settings.fov, 80), 40, 130);
    let target = baseFov;
    if (!forceBase) {
      const zoom = this._adsZoom();
      target = baseFov + (baseFov / zoom - baseFov) * ads + sprintKick;
      target = clamp(target, 15, 140);
    }
    if (Math.abs(camera.fov - target) > FOV_EPSILON) {
      camera.fov = target;
      camera.updateProjectionMatrix();
    }
  }

  /** Definicao da arma atual, aceitando as formas comuns de exposicao. */
  _weaponDef() {
    const w = this.ctx.weapons;
    if (!w) return null;
    const c = w.current;
    if (c && typeof c === 'object') return c.def && typeof c.def === 'object' ? c.def : c;
    return null;
  }

  /**
   * Zoom da mira como MAGNIFICACAO (>1). Aceita tambem a convencao inversa
   * (fator de FOV, ex.: 0.8) e converte, porque as duas aparecem em dados de arma.
   */
  _adsZoom() {
    const w = this.ctx.weapons;
    if (w && w.getAdsZoom) {
      const z = w.getAdsZoom();
      if (Number.isFinite(z) && z > 0.15) return z >= 1 ? clamp(z, 1, 8) : clamp(1 / z, 1, 8);
    }
    const def = this._weaponDef();
    if (def) {
      const v = num(def.adsZoom, num(def.zoom, num(def.adsFovScale, NaN)));
      if (Number.isFinite(v) && v > 0.15) return v >= 1 ? clamp(v, 1, 8) : clamp(1 / v, 1, 8);
    }
    return ADS_ZOOM_FALLBACK;
  }

  /** Tempo de entrada na mira, para o FOV acompanhar a arma. */
  _adsTime() {
    const def = this._weaponDef();
    if (def) {
      const v = num(def.adsTime, num(def.adsInTime, num(def.aimTime, NaN)));
      if (Number.isFinite(v) && v > 0.02) return v;
    }
    return ADS_TIME_FALLBACK;
  }

  /** Impacto de queda: mergulho do olho + aceno + tremor, tudo por velocidade. */
  _applyLanding(speed) {
    const s = num(speed, 0);
    if (s < LAND_MIN_SPEED) return;
    this._landCooldown = 0.12;   // evita contar o mesmo pouso pelo evento e pela deteccao
    const f = clamp01((s - LAND_MIN_SPEED) / (LAND_REF_SPEED - LAND_MIN_SPEED));
    const eased = f * (0.45 + 0.55 * f);   // queda curta quase nao mexe; queda alta pesa
    this._landVel -= LAND_IMPULSE * eased;
    if (eased > 0.12) this.addShake(0.05 + eased * 0.35, 0.20 + eased * 0.22);
  }

  /** Empurrao direcional ao levar dano: a cabeca joga para longe do tiro. */
  _hitKick(from, amount) {
    this._recPitchVel += 1.1 + amount * 2.2;
    if (!from || !Number.isFinite(from.x) || !Number.isFinite(from.z)) return;
    const dx = this._eyeX - from.x;
    const dz = this._eyeZ - from.z;
    const len = Math.sqrt(dx * dx + dz * dz);
    if (len < 0.001) return;
    const rightX = Math.cos(this.yaw);
    const rightZ = -Math.sin(this.yaw);
    const lat = (dx * rightX + dz * rightZ) / len;   // >0: fugir do tiro e ir para a direita
    this._recYawVel -= lat * (0.9 + amount * 1.8);
  }

  /** O jogador esta de fato jogando? Usado so para soltar a pose de depuracao. */
  _playerIsPlaying(input) {
    if (!input || !input.isDown) return false;
    return input.isDown('forward') || input.isDown('back') || input.isDown('left')
      || input.isDown('right') || input.isDown('jump')
      || (input.isMouseDown ? input.isMouseDown(0) : false);
  }

  dispose() {
    for (let i = 0; i < this._unsubs.length; i++) {
      try { this._unsubs[i](); } catch { /* ouvinte ja removido */ }
    }
    this._unsubs.length = 0;
  }
}
